import { useMemo, type ReactNode } from "react";
import {
    useRoom,
    useGeolocation,
    useTenantApp,
    useRoomOrchestration,
    usePositionAutoUpdate
} from "../hooks";
import { AppContext } from "../contexts/AppContext";
import { useMapBoundsCheck } from "@/hooks/geolocation/useMapBoundsCheck";

export const AppProvider = ({ children }: { children: ReactNode }) => {
    // recupero il tenant
    const { app } = useTenantApp()

    // utilizzo useRoom per la gestione della stanza
    const {
        createNewRoom,
        joinExistingRoom,
        updatePosition,
        removeUserFromRoom,
        saveBluetoothToRoom,
        roomSession,
        participants,
        loading: loadingOperationOnRoom,
    } = useRoom({ app });

    // memorizzazione dei confini della mappa
    const mapBounds = useMemo(() => {
        return app?.mapConfig.bounds || null;
    }, [app?.mapConfig.bounds]);

    // utilizzo useGeolocation per ottenere la posizione dell'utente
    const {
        position,
        geolocationStatus,
        loading: loadingGeolocation
    } = useGeolocation({ app });

    // controllo se l'utente è all'interno dei confini della mappa
    const isWithinBounds = useMapBoundsCheck(position, mapBounds);

    // Gestione orchestrazione stanze (registrazione, logout, join tramite QR)
    const { handleRegistration, handleLogout, handleJoinScannedRoom, handleAddBluetoothDevice } = useRoomOrchestration({
        app,
        position,
        roomSession,
        createNewRoom,
        joinExistingRoom,
        removeUserFromRoom,
        saveBluetoothToRoom
    });

    // aggiorna automaticamente la posizione dell'utente nella stanza quando cambia (con throttling interno)
    usePositionAutoUpdate({
        app,
        position,
        roomSession,
        updatePosition
    });

    // memorizzazione del valore del contesto per evitare ricalcoli inutili
    const value = useMemo(() => ({
        participants,
        loadingOperationOnRoom,
        loadingGeolocation,
        position,
        geolocationStatus,
        roomSession,
        isWithinBounds,
        handleRegistration,
        handleLogout,
        handleJoinScannedRoom,
        handleAddBluetoothDevice,
        updatePosition
    }), [
        participants,
        loadingOperationOnRoom,
        loadingGeolocation,
        position,
        geolocationStatus,
        roomSession,
        isWithinBounds,
        handleRegistration,
        handleLogout,
        handleJoinScannedRoom,
        handleAddBluetoothDevice,
        updatePosition
    ]);

    return (
        <AppContext.Provider value={value}>
            {children}
        </AppContext.Provider>
    )
};

